import {
    inject,
    Injectable,
    makeStateKey,
    NgZone,
    PendingTasks,
    REQUEST,
    StateKey,
    TransferState,
} from '@angular/core';
import { EnvironmentService } from '@core/services/environment.service';
import { InvalidationCacheEntry } from '@core/utils/invalidation-cache-entry';
import { ServerCache } from '@core/utils/server-cache';
import { shareTimedReplay } from '@core/utils/share-timed-replay';
import { ONEMONTH } from '@core/utils/timeConstants';
import { defer, Observable, of, timer } from 'rxjs';
import { shareReplay, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private serverCache = inject(ServerCache, { optional: true });
    private request = inject(REQUEST, { optional: true });
    private transferState = inject(TransferState);
    private environmentService = inject(EnvironmentService);
    private zone = inject(NgZone);
    private taskService = inject(PendingTasks);

    private cache = {};
    private ids: string[] = [];

    clear_cache() {
        this.cache = {};
    }

    clear_namespace(namespace: string) {
        this.cache[namespace] = null;
    }

    invalidate_cache_key(namespace, key: string) {
        this.cache[namespace][key] = null;
    }

    cacheWrapper<T>(
        namespace: string,
        key: string,
        useCache: boolean = false,
        observable: Observable<T>,
        duration: number = 60 * 1000
    ): Observable<T> {
        if (!useCache) {
            return observable;
        }
        if (this.environmentService.isBrowser()) {
            return this.handleCache(namespace, key, observable, duration);
        } else {
            const stateKey = this.stateKey(namespace, key);
            const cachedValue = this.handleCache(
                namespace,
                key,
                observable,
                duration
            );
            const saveCache = cachedValue
                .pipe(
                    switchMap(data =>
                        this.updateServerCache(stateKey, data, duration)
                    ),
                    take(1)
                )
                .subscribe();

            const saveIds = cachedValue
                .pipe(
                    switchMap(data =>
                        this.saveIds(stateKey, this.extractIds(data))
                    ),
                    take(1)
                )
                .subscribe();

            return cachedValue.pipe(
                tap(data => this.transferState.set(stateKey, data))
            );
        }
    }

    private async updateServerCache(
        stateKey: string,
        data: any,
        duration: number
    ) {
        return this.taskService.run(async () => {
            const cachedData = await this.serverCache.get(stateKey);
            if (data && !cachedData) {
                await this.serverCache.set(stateKey, data, duration);
            }
            return;
        });
    }

    private async saveIds(stateKey: string, ids: string[]) {
        return this.taskService.run(async () => {
            const url = this.request?.url;
            const invalidationKeys = ids.map(id => `CACHE_INVALIDATION_${id}`);
            if (invalidationKeys.length) {
                const invalidationEntries = (await this.serverCache.mget(
                    ...invalidationKeys
                )) as InvalidationCacheEntry[];
                const updatedInvalidationEntries = invalidationEntries.map(
                    value => {
                        if (value === undefined || value === null) {
                            return {
                                urls: [url],
                                keys: [stateKey],
                            };
                        } else {
                            return {
                                urls: value.urls.includes(url)
                                    ? value.urls
                                    : [...value.urls, url],
                                keys: value.keys.includes(stateKey)
                                    ? value.keys
                                    : [...value.keys, stateKey],
                            };
                        }
                    }
                );
                return this.serverCache.mset(
                    updatedInvalidationEntries.map((value, index) => [
                        invalidationKeys[index],
                        value,
                    ]),
                    ONEMONTH
                );
            }
        });
    }

    private handleCache(
        namespace: string,
        key: string,
        observable: Observable<any>,
        duration = 60 * 1000
    ) {
        const stateKey = this.stateKey(namespace, key);
        if (
            this.cache[namespace] === null ||
            this.cache[namespace] === undefined
        ) {
            this.cache[namespace] = {};
        }
        if (
            this.environmentService.isBrowser() &&
            this.transferState.hasKey(stateKey)
        ) {
            const transferData = this.getAndRemoveTransferState(stateKey);
            this.cache[namespace][key] = of(transferData);
            this.zone.runOutsideAngular(() =>
                timer(duration).subscribe(data =>
                    this.invalidate_cache_key(namespace, key)
                )
            );
        }
        if (
            !this.cache[namespace][key] &&
            this.environmentService.isServer() &&
            this.serverCache
        ) {
            this.cache[namespace][key] = defer(() =>
                this.taskService.run(() => this.serverCache.get(stateKey))
            ).pipe(
                switchMap(result =>
                    result
                        ? this.cachePipe(of(result), duration)
                        : this.cachePipe(observable, duration)
                )
            );
        }
        if (!this.cache[namespace][key]) {
            this.cache[namespace][key] = this.cachePipe(observable, duration);
        }
        return this.cache[namespace][key];
    }

    private stateKey(namespace, key) {
        return makeStateKey<string>(`${namespace}_${key}`);
    }

    private getAndRemoveTransferState(key: StateKey<string>, defaultValue?) {
        const data = this.transferState.get(key, defaultValue);
        this.transferState.remove(key);
        return data;
    }

    private cachePipe<T>(
        observable: Observable<T>,
        duration: number
    ): Observable<T> {
        if (this.environmentService.isServer()) {
            return observable.pipe(shareReplay(1));
        }
        return observable.pipe(shareTimedReplay(1, duration), take(1));
    }

    private extractIds(data: any) {
        const ids = this.recursiveKeyExtraction(data);
        return [...new Set(ids)];
    }

    private recursiveKeyExtraction(data: any) {
        let results = [];
        if (typeof data === 'string' || data === null || data === undefined) {
            return results;
        } else {
            for (let key of Object.keys(data)) {
                if (key === 'id') {
                    results.push(data['id']);
                } else {
                    const asdf = this.extractIds(data[key]);
                    if (asdf.length) {
                        results = results.concat(asdf);
                    }
                }
            }
        }
        return results;
    }
}
